import React, {useEffect, useState} from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import {useSelector, useDispatch} from "react-redux";
import SyncConstants from "../../constants/SyncConstants";
import {copy, getSlotDate} from "../../libs/SlotUtils";
import ExpenseEntryList from "./ExpenseEntryList";
import ExternalCostsList from "./ExternalCostsList";
import ExternalCostMask from "./ExternalCostMask";
import stylelist from "stylelist";
import {set_notification, set_sync_status} from "../../actions/Sync";
import NewProject from "./NewProject";
import {getFriday, getMonday} from "../../libs/CalendarWeek";
import MinutesToTime from "./MinutesToTime";
import {set_expense_project_root_id} from "../../actions/User";
import ExpenseMask from "../Slots/ExpenseMask";
import useAuthentication from "../../hooks/useAuthentication";
import {useParams} from "react-router-dom/cjs/react-router-dom.min";
import {generateRandomClassName} from "../../libs/StringUtil";
import MonthlyBreakdown from "./MonthlyBreakdown";
import SlideToConfirm from "../UI/SlideToConfirm";
import Raptorize from "../UI/Raptorize";

const ExpenseProject = (props) => {

    const UserSettings = useSelector((state) => state.User);
    const dispatch = useDispatch();
    const Team = useSelector((state) => state.Team);
    const CurrentUser = useSelector((state) => state.CurrentUser);
    const Auth = useAuthentication();

    // All server data (Expense Project Data)
    const [serverData, setServerData] = useState({});

    // Toggles if child projects are shown (Expanded)
    const [showChildren, setShowChildren] = useState(false);

    // Toggles if booked expense entries are shown (recursive for all sub-projects)
    const [showExpenseEntries, setShowExpenseEntries] = useState(false);

    // Toggles if external costs are shown
    const [showExternalCosts, setShowExternalCosts] = useState(false);

    const [showProjectBreakdown, setShowProjectBreakdown] = useState(false);

    // Filter-value for "Date from"
    const [filterDateFrom, setFilterDateFrom] = useState(props.filterDateFrom);

    // Filter-value for "Date to"
    const [filterDateTo, setFilterDateTo] = useState(props.filterDateTo);

    const params = useParams();

    // Filter-value for "Person"
    const [filterPerson, setFilterPerson] = useState(props.filterDatePerson);

    const [projectEditMode, setProjectEditMode] = useState(false);

    const [separateByDays, setSeparateByDays] = useState(false);

    const [showNewProject, setShowNewProject] = useState(false);

    const [progressMinutes, setProgressMinutes] = useState(null);

    const [showEditProjectId, setShowEditProjectId] = useState(false);

    const [expandView, setExpandView] = useState(false);

    const [loading, setLoading] = useState(false);

    const [expenseMaskId, setExpenseMaskId] = useState(0);
    
    const [showNewExternalCost, setShowNewExternalCost] = useState(false);

    const [searchword, setSearchword] = useState("");

    const [autoExpandUsed, setAutoExpandUsed] = useState(false);

    const [openThisId, setOpenThisId] = useState(false);
    const [openChildId, setOpenChildId] = useState(false);

    const randomClassName = generateRandomClassName();

    const [currentPath, setCurrentPath] = useState("");

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [triggerRaptorize, setTriggerRaptorize] = useState(false);
    
    const [showArchivedProjects, setShowArchivedProjects] = useState(false);

    // Helper function to get emoji based on project type
    const getProjectEmoji = (projectType) => {
        switch(projectType) {
            case 'customer':
                // Randomly choose between male and female business person emoji
                return Math.random() < 0.5 ? '🧑‍💼 ' : '👩‍💼 ';
            case 'serviceproject':
                return '🔧 ';
            case 'externalcosts':
                return '💶 ';
            case 'project':
            default:
                return '📁 ';
        }
    };
    
    // Helper function to get CSS filter for folder color
    const getFolderColorStyle = (projectType, portalColor) => {
        if (projectType !== 'project' || !portalColor) {
            return {};
        }
        
        // CSS filters to colorize the folder emoji
        const colorFilters = {
            'red': { filter: 'hue-rotate(307deg) saturate(5) brightness(1)' },
            'yellow': { filter: 'hue-rotate(357deg) saturate(1.5) brightness(1.1)' },
            'green': { filter: 'hue-rotate(48deg) saturate(1.5) brightness(0.95)' },
            'blue': { filter: 'hue-rotate(180deg) saturate(1.5) brightness(0.95)' },
            'purple': { filter: 'hue-rotate(240deg) saturate(1.5) brightness(0.95)' },
            'gray': { filter: 'saturate(0) brightness(1.05)' }
        };
        
        return colorFilters[portalColor] || {};
    };

    // Run after initializing
    const init = useEffect(() => {
        // Load all child projects from the server
        loadFromServer();

        // Dispatch a global sync (to make sure we have team members etc.)
        // But not in selectMode, as this is used in modals where sync might fail
        if (props.level == 1 && !props.selectMode) {
            dispatch(set_sync_status("asap_full"));
        }

        // Show children if we are on level 1 or 2, so user dont have to expand it manually
        if (props.level == 1) {
            setShowChildren(true);
        }

        if (params.id !== undefined) {
            let parts;

            if (props.level == 1) {
                // Remove "-folders" suffix if present to get the actual project ID
                const cleanId = params.id.replace(/-folders$/, '');
                parts = cleanId.split("-");
                setCurrentPath(1 + "-");
                setSearchword(parts[1]);
            } else {
                parts = props.remainingUrlPath.split("-");
            }

            const first = parts[0];
            const rest = parts.slice(1).join("-");

            setOpenThisId(first);
            setOpenChildId(rest);

            if (props.expenseProjectId == first) {
                setShowChildren(true);
            }

            // Only show expense entries if URL does NOT contain "-folders" suffix
            if (rest == false && props.expenseProjectId == first && !params.id.includes('-folders')) {
                setShowExpenseEntries(true);
            }
        }

        let currentPath = props.parentPath + "-" + props.expenseProjectId;

        // Entferne den ersten Bindestrich, falls vorhanden
        if (currentPath.startsWith("-")) {
            currentPath = currentPath.substring(1);
        }

        setCurrentPath(currentPath);
    }, []);

    /* const reforce = useEffect(() => {
         loadFromServer();
     }, [props.expenseProjectId]);*/

    useEffect(() => {
        if (props.level > 0) {
            setProjectEditMode(props.projectEditMode);
            setSeparateByDays(props.separateByDays);
            setShowArchivedProjects(props.showArchivedProjects);
        }
    }, [props.projectEditMode, props.separateByDays, props.showArchivedProjects]);

    // Reload when showArchivedProjects changes
    useEffect(() => {
        if (props.level > 0) {
            loadFromServer();
        }
    }, [showArchivedProjects]);

    const updateProgressMinutes = () => {
        if (props.level > 2) {
            axios({
                method: "get",
                auth: Auth.getAuthObject(),
                url: SyncConstants["syncBaseURL"] + "expenseEntries/sum",
                params: {
                    project_id: props.expenseProjectId
                },
                responseType: 'json'
            }).then((response) => {

                let total = response.data.calculation.total;
                if (total > 0) {
                    setProgressMinutes(total);
                    setProgressPercentage((100 / serverData.parent[0].maxminutes * response.data.calculation.total));
                } else {
                    setProgressMinutes(0);
                    setProgressPercentage(0);
                }

            }).catch(response => {
            });
        }
    }

    const filterToCurrentWeek = () => {
        setFilterDateFrom(getSlotDate(getMonday(new Date()).toDate()));
        setFilterDateTo(getSlotDate(getFriday(new Date()).toDate()));
    }

    useEffect(e => {
        const search = props.searchword?.trim().toLowerCase() ?? '';
        const parentTitle = serverData.parent?.[0]?.title?.toLowerCase() ?? '';
        const hasValidSearch = (search.length >= 3 && parentTitle.includes(search)) || serverData?.parent?.[0]?.id == search;
        const hasChildrenAndParent = !!serverData.children && !!serverData.parent;

        if (!hasChildrenAndParent) return;

        if (hasValidSearch) {
            setShowChildren(true);
            setAutoExpandUsed(true);
        } else if (autoExpandUsed && search === '') {
            setShowChildren(false);
            setAutoExpandUsed(false);
        }


    }, [props.searchword, searchword]);

    // Set filter-values to inherited props
    useEffect(e => {
        setFilterDateFrom(props.filterDateFrom);
        setFilterDateTo(props.filterDateTo);
        setFilterPerson(props.filterPerson);
    }, [props.filterDateFrom, props.filterDateTo, props.filterPerson])

    // Get all expenseProjects by this ID from the Server
    // @todo: Add error handling and so on
    const loadFromServer = () => {
        setLoading(true);
        axios({
            method: "get",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "expenseProjects/byParent",
            params: {
                parent: props.expenseProjectId,
                includeArchived: showArchivedProjects
            },
            responseType: 'json'
        }).then((response) => {
            // Sort children: service projects first, then external costs, then others
            if (response.data.children) {
                response.data.children.sort((a, b) => {
                    // Service projects come first
                    if (a.project_type === 'serviceproject' && b.project_type !== 'serviceproject') return -1;
                    if (a.project_type !== 'serviceproject' && b.project_type === 'serviceproject') return 1;
                    // External costs come second
                    if (a.project_type === 'externalcosts' && b.project_type !== 'externalcosts' && b.project_type !== 'serviceproject') return -1;
                    if (a.project_type !== 'externalcosts' && b.project_type === 'externalcosts' && a.project_type !== 'serviceproject') return 1;
                    // Then sort alphabetically by title
                    return a.title.localeCompare(b.title);
                });
            }
            setServerData(response.data);
            updateProgressMinutes();
            setLoading(false);
        }).catch(response => {
        });
    }

    const handleArchiveProject = () => {
        axios({
            method: "post",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "expenseProject",
            responseType: 'json',
            data: {
                id: serverData.parent[0].id,
                status: 2 // Status 2 = Archiviert
            }
        }).then((response) => {
            dispatch(set_notification("Projekt wurde archiviert.", "success"));
            setShowArchiveConfirm(false);

            // Reload parent to reflect archival
            if (props.parentLoadFromServer) {
                props.parentLoadFromServer();
            }

            // If this is top level, refresh the page
            if (props.level === 1) {
                dispatch(set_sync_status("asap_full"));
            }
        }).catch((error) => {
            dispatch(set_notification("Fehler beim Archivieren des Projekts: " + (error.response?.data?.error || error.message), "danger"));
            setShowArchiveConfirm(false);
        });
    }

    const handleDeleteProject = () => {
        axios({
            method: "delete",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "expenseProject",
            params: {
                id: serverData.parent[0].id
            },
            responseType: 'json'
        }).then((response) => {
            dispatch(set_notification("Projekt gelöscht. Verwaiste Daten werden durch den Maintenance-Job bereinigt.", "success"));
            setShowDeleteConfirm(false);

            // Trigger Raptorize!
            setTriggerRaptorize(true);

            // Wait for raptor to finish before reloading
            setTimeout(() => {
                // Reload parent to reflect deletion
                if (props.parentLoadFromServer) {
                    props.parentLoadFromServer();
                }

                // If this is top level, refresh the page
                if (props.level === 1) {
                    dispatch(set_sync_status("asap_full"));
                }

                setTriggerRaptorize(false);
            }, 3000);
        }).catch((error) => {
            dispatch(set_notification("Fehler beim Löschen des Projekts: " + (error.response?.data?.error || error.message), "danger"));
            setShowDeleteConfirm(false);
        });
    }

    return <>
        {/* Raptorize Effect */}
        <Raptorize trigger={triggerRaptorize} />

        {/* Archive Confirmation Modal */}
        <SlideToConfirm
            show={showArchiveConfirm}
            onConfirm={handleArchiveProject}
            onCancel={() => setShowArchiveConfirm(false)}
            title="Projekt archivieren?"
            message="Das Projekt wird archiviert und verschwindet aus der aktiven Projektliste. Es kann später über die Projekt-Einstellungen wieder aktiviert werden."
            slideText="Zum Archivieren schieben"
            variant="warning"
        />

        {/* Delete Confirmation Modal */}
        <SlideToConfirm
            show={showDeleteConfirm}
            onConfirm={handleDeleteProject}
            onCancel={() => setShowDeleteConfirm(false)}
            title="Projekt wirklich löschen?"
            message="ACHTUNG: Projekte sollten normalerweise nur archiviert werden! Das Löschen eines Projekts ist endgültig und kann nicht rückgängig gemacht werden. Verwaiste Aufwände und Unterprojekte werden durch den Maintenance-Job entfernt."
            slideText="Zum Löschen schieben"
            variant="danger"
        />

        {/* Modals - using Portal to render directly to body */}
        {showEditProjectId > 0 && ReactDOM.createPortal(
            <div
                className="overlay d-flex justify-content-center align-items-center"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    backgroundColor: 'rgba(0,0,0,0.5)'
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowEditProjectId(false);
                    }
                }}
            >
                <div style={{ width: '90%', maxWidth: '1200px', maxHeight: '90vh', overflowY: 'auto' }}>
                    <NewProject editId={showEditProjectId} afterSubmit={(needDeepRefresh) => {
                        loadFromServer();
                        setShowEditProjectId(false);
                        props.parentLoadFromServer();
                        if (needDeepRefresh) {
                            props.parentLoadFromServer();
                        }
                    }}/>
                </div>
            </div>,
            document.body
        )}

        {(showNewProject && projectEditMode) && ReactDOM.createPortal(
            <div
                className="overlay d-flex justify-content-center align-items-center"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    backgroundColor: 'rgba(0,0,0,0.5)'
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowNewProject(false);
                    }
                }}
            >
                <div style={{ width: '90%', maxWidth: '1200px', maxHeight: '90vh', overflowY: 'auto' }}>
                    <NewProject projectParentId={serverData.parent[0].id} afterSubmit={(needDeepRefresh) => {
                        loadFromServer();
                        setShowNewProject(false);
                        /*props.parentLoadFromServer();*/
                    }}/>
                </div>
            </div>,
            document.body
        )}

        {/* Start: The first level of our tree. We will render some global controls here */}
        {(props.level == 1 && !props.selectMode) &&
            <>
                <div className="d-flex justify-content-between">
                    <h1>Arbeitsaufwände</h1>
                </div>

                {/* Compact Toggle Toolbar with Breadcrumb */}
                <div className="bg-light border rounded shadow-sm p-3 mb-3">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                        {/* Breadcrumb on the left - can wrap */}
                        <div className="d-flex align-items-center flex-wrap gap-1 flex-grow-1">
                            {props.breadcrumb}
                        </div>
                        
                        {/* Buttons on the right - stay on the right */}
                        <div className="btn-group btn-group-sm flex-shrink-0" role="group">
                                <input
                                    type="checkbox"
                                    className="btn-check"
                                    id="toggleProjectEdit"
                                    autoComplete="off"
                                    checked={projectEditMode}
                                    onChange={e => setProjectEditMode(e.target.checked)}
                                />
                                <label
                                    className={`btn ${projectEditMode ? 'btn-primary' : 'btn-outline-primary'}`}
                                    htmlFor="toggleProjectEdit"
                                    title="Projektstruktur editieren"
                                >
                                    <i className="bi bi-pencil-square me-1"></i>
                                    Struktur editieren
                                </label>

                                <input
                                    type="checkbox"
                                    className="btn-check"
                                    id="toggleExpenseBucket"
                                    autoComplete="off"
                                    checked={props.expenseBucket}
                                    onChange={e => props.setShowExpenseBucket(!props.expenseBucket)}
                                />
                                <label
                                    className={`btn ${props.expenseBucket ? 'btn-success' : 'btn-outline-success'}`}
                                    htmlFor="toggleExpenseBucket"
                                    title="Abrechnungsmodus aktivieren"
                                >
                                    <i className="bi bi-receipt me-1"></i>
                                    Abrechnung
                                </label>

                                <input
                                    type="checkbox"
                                    className="btn-check"
                                    id="toggleSeparateDays"
                                    autoComplete="off"
                                    checked={separateByDays}
                                    onChange={e => setSeparateByDays(e.target.checked)}
                                />
                                <label
                                    className={`btn ${separateByDays ? 'btn-info' : 'btn-outline-info'}`}
                                    htmlFor="toggleSeparateDays"
                                    title="Aufwände nach Tagen gruppieren"
                                >
                                    <i className="bi bi-calendar3 me-1"></i>
                                    Nach Tagen
                                </label>

                                <input
                                    type="checkbox"
                                    className="btn-check"
                                    id="toggleArchived"
                                    autoComplete="off"
                                    checked={showArchivedProjects}
                                    onChange={e => setShowArchivedProjects(e.target.checked)}
                                />
                                <label
                                    className={`btn ${showArchivedProjects ? 'btn-warning' : 'btn-outline-warning'}`}
                                    htmlFor="toggleArchived"
                                    title="Archivierte Projekte anzeigen"
                                >
                                    <i className="bi bi-archive me-1"></i>
                                    Archiv
                                </label>
                            </div>
                    </div>
                </div>

                    {/* Modern Filter Toolbar */}
                    <div className="bg-light border rounded shadow-sm p-2 mb-3">
                        <div className="card-body p-2">
                            <div className="row g-2 align-items-end">
                                {/* Search/Customer Filter - only show when NOT in direct link mode */}
                                {!props.isDirectLink && (
                                    <div className="col-lg-3 col-md-6">
                                        <label className="form-label mb-1 small text-muted">
                                            <i className="bi bi-search me-1"></i>
                                            Kunde / Suche
                                        </label>
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-light border-end-0">
                                                <i className="bi bi-building text-muted"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control border-start-0 ps-2"
                                                placeholder="Kundenname..."
                                                value={searchword}
                                                onChange={e => setSearchword(e.target.value)}
                                            />
                                            {searchword && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setSearchword("")}
                                                    title="Suche löschen"
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Date From */}
                                <div className={props.isDirectLink ? "col-lg-4 col-md-6" : "col-lg-3 col-md-6"}>
                                    <label className="form-label mb-1 small text-muted">
                                        <i className="bi bi-calendar-event me-1"></i>
                                        Von
                                    </label>
                                    <div className="input-group input-group-sm">
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={filterDateFrom}
                                            onChange={e => setFilterDateFrom(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Date To */}
                                <div className={props.isDirectLink ? "col-lg-4 col-md-6" : "col-lg-3 col-md-6"}>
                                    <label className="form-label mb-1 small text-muted">
                                        <i className="bi bi-calendar-check me-1"></i>
                                        Bis
                                    </label>
                                    <div className="input-group input-group-sm">
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={filterDateTo}
                                            onChange={e => setFilterDateTo(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Person Filter */}
                                <div className={props.isDirectLink ? "col-lg-4 col-md-6" : "col-lg-3 col-md-6"}>
                                    <label className="form-label mb-1 small text-muted">
                                        <i className="bi bi-person me-1"></i>
                                        Person
                                    </label>
                                    <div className="input-group input-group-sm">
                                        <select
                                            value={filterPerson}
                                            className="form-select"
                                            onChange={e => setFilterPerson(e.target.value)}
                                        >
                                            <option value="">Alle Personen</option>
                                            {Object.values(Team).sort((a, b) => {
                                                const lastNameA = a.realName.split(' ').pop().toLowerCase();
                                                const lastNameB = b.realName.split(' ').pop().toLowerCase();
                                                return lastNameA.localeCompare(lastNameB);
                                            }).map(m => (
                                                m.status === "active" && (
                                                    <option key={m.userName} value={m.userName}>
                                                        {m.realName}
                                                    </option>
                                                )
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                {Boolean(UserSettings.currentExpenseProjectRootId != 1 && serverData.parent != undefined) &&
                    <>
                        <div className="bg-light border ps-3 pe-3 pt-2 pb-2 mb-4">
                            Projekt <b>{serverData.parent[0].title}</b> als Root-Knoten gesetzt.
                        </div>
                    </>
                }
            </>
        }
        {/* End: The first level of our tree. We will render some global controls here */}

        <div className={stylelist(
            "container pe-0 m-0 p-0 mt-2",
            [props.level === 1, "ms-0 mt-4 pt-5"],
        )}>
            <div className={stylelist(
                [props.level === 1, "ms-0 mt-4 pt-5"],
                [props.level > 1, "ms-3"]
            )}>
                {!loading &&
                    <>
                        {Boolean((serverData.children !== undefined && serverData.parent !== undefined) &&
                                (!props.searchword || serverData.parent[0].title.toLowerCase().indexOf(props.searchword.toLowerCase()) > -1 || serverData.parent[0].id == props.searchword)
                            ) &&
                            <>

                                <div className={stylelist(
                                    "card shadow-sm mb-2",
                                    randomClassName,
                                    [serverData.parent[0].project_type === 'serviceproject', "bg-light"],
                                    [serverData.parent[0].project_type === 'externalcosts', "bg-light"]
                                )}
                                style={{
                                    opacity: serverData.parent[0].status === 2 ? 0.6 : 1
                                }}>
                                    <div className="card-body p-2">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center flex-grow-1">
                                                {/* Collapse/Expand Chevron */}
                                                {serverData.children.length > 0 ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link text-primary p-0 me-2"
                                                        onClick={e => setShowChildren(!showChildren)}
                                                        style={{width: '24px', textAlign: 'center'}}
                                                    >
                                                        <i className={`bi bi-chevron-${showChildren ? 'down' : 'right'} fw-bold`}></i>
                                                    </button>
                                                ) : (
                                                    <span style={{width: '24px', display: 'inline-block'}} className="me-2"></span>
                                                )}

                                                {/* Project Icon & Title */}
                                                <div className="d-flex align-items-center flex-grow-1">
                                                    <span
                                                        className="me-2 fs-5"
                                                        style={getFolderColorStyle(serverData.parent[0].project_type, serverData.parent[0].portalcolor)}
                                                    >
                                                        {getProjectEmoji(serverData.parent[0].project_type)}
                                                    </span>

                                                    {!props.selectMode ? (
                                                        <span
                                                            className="fw-semibold cursor-pointer flex-grow-1"
                                                            onClick={e => {
                                                                if (serverData.children.length > 0) {
                                                                    setShowChildren(!showChildren);
                                                                } else {
                                                                    setShowExpenseEntries(!showExpenseEntries);
                                                                }
                                                            }}
                                                        >
                                                            {serverData.parent[0].title}
                                                            {serverData.parent[0].status === 2 && (
                                                                <span className="badge bg-secondary ms-2">
                                                                    <i className="bi bi-archive me-1"></i>
                                                                    Archiviert
                                                                </span>
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <span
                                                                className={stylelist(
                                                                    "fw-semibold",
                                                                    [serverData.parent[0].bookable, "cursor-pointer"],
                                                                    [!serverData.parent[0].bookable, "text-muted"]
                                                                )}
                                                                onClick={e => {
                                                                    if (serverData.parent[0].bookable) {
                                                                        props.onSelect(serverData.parent[0]);
                                                                    }
                                                                }}
                                                            >
                                                                {serverData.parent[0].title}
                                                            </span>
                                                            {!serverData.parent[0].bookable && (
                                                                <small className="ms-auto badge bg-secondary">Nicht buchbar</small>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Actions & Progress */}
                                            <div className="d-flex align-items-center gap-2">
                                                {/* Progress Bar */}
                                                {Boolean(serverData.parent[0].maxminutes > 0) && (
                                                    <div className="d-flex align-items-center me-3">
                                                        <div className="progress" style={{width: '100px', height: '8px'}}>
                                                            <div
                                                                className={`progress-bar ${
                                                                    (progressMinutes / serverData.parent[0].maxminutes) > 1 ? 'bg-danger' :
                                                                    (progressMinutes / serverData.parent[0].maxminutes) > 0.8 ? 'bg-warning' : 'bg-success'
                                                                }`}
                                                                role="progressbar"
                                                                style={{width: `${Math.min((progressMinutes / serverData.parent[0].maxminutes) * 100, 100)}%`}}
                                                            ></div>
                                                        </div>
                                                        <small className="text-muted ms-2">
                                                            {Math.round((progressMinutes / serverData.parent[0].maxminutes) * 100)}%
                                                        </small>
                                                    </div>
                                                )}

                                                {/* Action Buttons */}
                                                {!projectEditMode && serverData.parent[0].status !== 2 && (
                                                    <div className="btn-group btn-group-sm">
                                                        {(!showExpenseEntries && !showProjectBreakdown && !props.selectMode) && (
                                                            <>
                                                                {Boolean(UserSettings.currentExpenseProjectRootId == serverData.parent[0].id && serverData.parent[0].id != 1) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary"
                                                                        onClick={e => {
                                                                            dispatch(set_expense_project_root_id(1));
                                                                            window.scrollTo(0, 0);
                                                                        }}
                                                                        title="Nicht mehr als Root-Knoten verwenden"
                                                                    >
                                                                        <i className="bi bi-x-circle"></i>
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary"
                                                                        onClick={e => {
                                                                            dispatch(set_expense_project_root_id(serverData.parent[0].id));
                                                                            window.scrollTo(0, 0);
                                                                        }}
                                                                        title="Als Root-Knoten anzeigen"
                                                                    >
                                                                        <i className="bi bi-house"></i>
                                                                    </button>
                                                                )}

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-info"
                                                                    onClick={e => {
                                                                        const url = `${window.location.origin}/expenses/${serverData.parent[0].id}`;
                                                                        navigator.clipboard.writeText(url).then(() => {
                                                                            dispatch(set_notification("Direktlink kopiert!", "success"));
                                                                        }).catch(() => {
                                                                            dispatch(set_notification("Fehler beim Kopieren", "error"));
                                                                        });
                                                                    }}
                                                                    title="Direktlink zu diesem Projekt kopieren"
                                                                >
                                                                    <i className="bi bi-link-45deg"></i>
                                                                </button>

                                                                {((props.level > 2) || (props.level <= 2 && CurrentUser.data && CurrentUser.data.role === 'administrator')) && (
                                                                    <>
                                                                        {/* Aufwände Button - nicht bei externalcosts Projekten */}
                                                                        {serverData.parent[0].project_type !== 'externalcosts' && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-primary"
                                                                                onClick={e => {
                                                                                    setShowExpenseEntries(!showExpenseEntries);
                                                                                    setShowExternalCosts(false);
                                                                                    setShowProjectBreakdown(false);
                                                                                    setShowNewProject(false);
                                                                                }}
                                                                                title="Aufwände anzeigen"
                                                                            >
                                                                                <i className="bi bi-list-ul"></i>
                                                                            </button>
                                                                        )}
                                                                        
                                                                        {/* External Costs Button - nur bei externalcosts Projekten */}
                                                                        {serverData.parent[0].project_type === 'externalcosts' && !showExternalCosts && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-success"
                                                                                onClick={e => {
                                                                                    setShowExternalCosts(!showExternalCosts);
                                                                                    setShowExpenseEntries(false);
                                                                                    setShowProjectBreakdown(false);
                                                                                    setShowNewProject(false);
                                                                                }}
                                                                                title="Externe Kosten anzeigen"
                                                                            >
                                                                                <i className="bi bi-currency-euro"></i>
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {/* Breakdown Button - nicht bei externalcosts Projekten */}
                                                                {!showProjectBreakdown && serverData.parent[0].project_type !== 'externalcosts' && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-info"
                                                                        onClick={() => {
                                                                            setShowProjectBreakdown(true);
                                                                            setShowExpenseEntries(false);
                                                                            setShowNewProject(false);
                                                                        }}
                                                                        title="Projekt Breakdown anzeigen"
                                                                    >
                                                                        <i className="bi bi-calendar2"></i>
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {(showExpenseEntries || showExternalCosts || showProjectBreakdown) && !props.selectMode && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={e => {
                                                                    setShowExpenseEntries(false);
                                                                    setShowExternalCosts(false);
                                                                    setShowProjectBreakdown(false);
                                                                }}
                                                            >
                                                                <i className="bi bi-x-lg me-1"></i>
                                                                Schließen
                                                            </button>
                                                        )}
                                                    </div>
                                                )}

                                                {projectEditMode && (
                                                    <div className="btn-group btn-group-sm ms-2">
                                                        {(!showNewProject && showEditProjectId < 1) && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-success"
                                                                    onClick={e => setShowNewProject(true)}
                                                                    title="Neues Projekt anlegen"
                                                                >
                                                                    <i className="bi bi-plus-lg me-1"></i>
                                                                    Neu
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-warning"
                                                                    onClick={e => setShowEditProjectId(serverData.parent[0].id)}
                                                                    title="Projekt bearbeiten"
                                                                >
                                                                    <i className="bi bi-pencil me-1"></i>
                                                                    Bearbeiten
                                                                </button>
                                                                {serverData.parent[0].id !== 1 && serverData.parent[0].status !== 2 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary"
                                                                        onClick={e => setShowArchiveConfirm(true)}
                                                                        title="Projekt archivieren"
                                                                    >
                                                                        <i className="bi bi-archive me-1"></i>
                                                                        Archivieren
                                                                    </button>
                                                                )}
                                                                {serverData.parent[0].id !== 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger"
                                                                        onClick={e => setShowDeleteConfirm(true)}
                                                                        title="Projekt löschen (normalerweise sollte archiviert werden!)"
                                                                    >
                                                                        <i className="bi bi-trash me-1"></i>
                                                                        Löschen
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {showEditProjectId > 0 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={e => setShowEditProjectId(false)}
                                                                title="Bearbeitung schließen"
                                                            >
                                                                <i className="bi bi-x-lg me-1"></i>
                                                                Schließen
                                                            </button>
                                                        )}

                                                        {showNewProject && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={e => setShowNewProject(!showNewProject)}
                                                                title="Neu-Dialog schließen"
                                                            >
                                                                <i className="bi bi-x-lg me-1"></i>
                                                                Schließen
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {showProjectBreakdown &&
                                    <>
                                        <div className="border mt-2 mb-2 pt-3 pb-3 bg-light p-3 ps-4 pe-4 entry-list">
                                            <MonthlyBreakdown project_id={serverData.parent[0].id}/>
                                            <br/>
                                        </div>
                                    </>
                                }

                                {showChildren && !showExpenseEntries && !showExternalCosts &&
                                    <>

                                        {Object.values(serverData.children).filter(e => {
                                                return showArchivedProjects ? true : e.status == 1
                                            }).length == 0 &&
                                            <>
                                                <div className="alert alert-info me-3 mt-2 ms-3">
                                                    <b>Alle Unterprojekte sind archiviert.</b> Möglicherweise kann das Hauptprojekt auch archiviert werden.
                                                </div>
                                            </>
                                        }

                                        {/* Start: Show all child-elements */}
                                        {Object.values(serverData.children).filter(e => {
                                            return showArchivedProjects ? true : e.status == 1
                                        }).map(iteration => {
                                            return <React.Fragment key={iteration.id}>
                                                <ExpenseProject
                                                    expenseProjectId={iteration.id}
                                                    level={parseInt(props.level) + 1}
                                                    filterDateFrom={filterDateFrom}
                                                    filterDateTo={filterDateTo}
                                                    filterPerson={filterPerson}
                                                    selectMode={props.selectMode}
                                                    onSelect={props.onSelect}
                                                    projectEditMode={projectEditMode}
                                                    separateByDays={separateByDays}
                                                    showArchivedProjects={showArchivedProjects}
                                                    expenseBucket={props.expenseBucket}
                                                    searchword={searchword}
                                                    remainingUrlPath={openChildId}
                                                    parentPath={currentPath}
                                                    parentLoadFromServer={() => {
                                                        loadFromServer()
                                                    }}
                                                />

                                            </React.Fragment>
                                        })}
                                        {/* End: Show all child-elements */}

                                    </>
                                }


                                {/* Start: Show all Expense Entries ("Aufwände") to this project */}
                                {showExpenseEntries &&
                                    <>
                                        <div className={stylelist(
                                            "card shadow-sm mt-2 mb-2 bg-grey",
                                            [expandView, "expand-absolute"]
                                        )}>
                                            <div className="card-header border-bottom pt-3 pb-3">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h5 className="mb-1">
                                                            <span style={getFolderColorStyle(serverData.parent[0].project_type, serverData.parent[0].portalcolor)}>
                                                                {getProjectEmoji(serverData.parent[0].project_type)}
                                                            </span>
                                                            &nbsp;{serverData.parent[0].title}
                                                            <small className="text-muted ms-2">inkl. Unterprojekte</small>
                                                        </h5>
                                                        {serverData.parent[0].maxminutes > 0 && (
                                                            <div className="d-flex align-items-center gap-3 mt-2">
                                                                <span className={stylelist(
                                                                    "badge",
                                                                    [((progressMinutes / serverData.parent[0].maxminutes) * 100) > 100, "bg-danger"],
                                                                    [((progressMinutes / serverData.parent[0].maxminutes) * 100) > 80 && ((progressMinutes / serverData.parent[0].maxminutes) * 100) <= 100, "bg-warning"],
                                                                    [((progressMinutes / serverData.parent[0].maxminutes) * 100) <= 80, "bg-success"]
                                                                )}>
                                                                    <i className="bi bi-clock me-1"></i>
                                                                    <MinutesToTime minutes={progressMinutes > 0 ? progressMinutes : 0}/>
                                                                    &nbsp;/ <MinutesToTime minutes={serverData.parent[0].maxminutes}/>
                                                                </span>
                                                                <span className="badge bg-light text-dark border">
                                                                    {((progressMinutes / serverData.parent[0].maxminutes) * 100).toFixed(1)}%
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="btn-group btn-group-sm">


                                                        {expenseMaskId > 0 ? (
                                                            <button
                                                                type="button"
                                                                title="Aufwandsmaske schließen"
                                                                onClick={() => setExpenseMaskId(0)}
                                                                className="btn btn-secondary"
                                                            >
                                                                <i className="bi bi-x-lg"></i>
                                                            </button>
                                                        ) : (
                                                            <>
                                                                {serverData.parent[0].status !== 2 && (
                                                                    <button
                                                                        type="button"
                                                                        title="Aufwand hinzufügen"
                                                                        onClick={() => setExpenseMaskId(serverData.parent[0].id)}
                                                                        className="btn btn-outline-success"
                                                                    >
                                                                        <i className="bi bi-plus-lg"></i>
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                       
                                                    </div>
                                                </div>
                                            </div>


                                            {expenseMaskId == serverData.parent[0].id && (
                                                <div className="card-body border-bottom bg-light">
                                                    <ExpenseMask
                                                        slotId=""
                                                        afterSubmit={() => {
                                                            setExpenseMaskId(0);
                                                            loadFromServer();
                                                        }}
                                                        forProject={serverData.parent[0]}
                                                        hideDeleteButton={true}
                                                    />
                                                </div>
                                            )}

                                            <div className="card-body p-0">
                                                <ExpenseEntryList
                                                    project={serverData.parent[0].id}
                                                    filterDateFrom={filterDateFrom}
                                                    filterDateTo={filterDateTo}
                                                    filterPerson={filterPerson}
                                                    separateByDays={separateByDays}
                                                    expandView={expandView}
                                                    expenseBucket={props.expenseBucket}
                                                    parentPath={currentPath}
                                                />
                                            </div>
                                        </div>
                                    </>
                                }
                                {/* End: Show all Expense Entries ("Aufwände") to this project */}

                                {/* Start: Show all External Costs to this project */}
                                {showExternalCosts &&
                                    <>
                                        <div className={stylelist(
                                            "card shadow-sm mt-2 mb-2 bg-grey",
                                            [expandView, "expand-absolute"]
                                        )}>
                                            <div className="card-header border-bottom pt-3 pb-3">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h5 className="mb-1">
                                                            💶&nbsp;{serverData.parent[0].title} - Externe Kosten
                                                            <small className="text-muted ms-2">inkl. Unterprojekte</small>
                                                        </h5>
                                                    </div>

                                                    <div className="btn-group btn-group-sm">
                                                        {showNewExternalCost ? (
                                                            <button
                                                                type="button"
                                                                title="Maske schließen"
                                                                onClick={() => setShowNewExternalCost(false)}
                                                                className="btn btn-secondary"
                                                            >
                                                                <i className="bi bi-x-lg"></i>
                                                            </button>
                                                        ) : (
                                                            <>
                                                                {serverData.parent[0].status !== 2 && (
                                                                    <button
                                                                        type="button"
                                                                        title="Externe Kosten hinzufügen"
                                                                        onClick={() => setShowNewExternalCost(true)}
                                                                        className="btn btn-outline-success"
                                                                    >
                                                                        <i className="bi bi-plus-lg"></i>
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {showNewExternalCost && (
                                                <div className="card-body border-bottom bg-light">
                                                    <ExternalCostMask
                                                        projectId={serverData.parent[0].id}
                                                        onSuccess={() => {
                                                            setShowNewExternalCost(false);
                                                            loadFromServer();
                                                        }}
                                                        onCancel={() => setShowNewExternalCost(false)}
                                                    />
                                                </div>
                                            )}

                                            <div className="card-body p-0">
                                                <ExternalCostsList
                                                    project={serverData.parent[0].id}
                                                    filterDateFrom={filterDateFrom}
                                                    filterDateTo={filterDateTo}
                                                    filterPerson={filterPerson}
                                                    expenseBucket={props.expenseBucket}
                                                />
                                            </div>
                                        </div>
                                    </>
                                }
                                {/* End: Show all External Costs to this project */}
                            </>
                        }
                    </>}

                {loading &&
                    <>

                        <div className="d-flex justify-content-between bg-on-hover pt-2">
                            <h3 className={stylelist(
                                [props.level == 1, "h6"],
                                [props.level == 2, "h6"],
                                [props.level == 3, "h6"],
                                [props.level > 3, "h6"],
                            )}>

                                <span className="text-primary cursor-pointer" onClick={e => {
                                    setShowChildren(true);
                                }}><i className="bi bi-plus"></i></span>

                                <div className="load-elipsis">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            </h3>
                        </div>
                    </>}
            </div>
        </div>
    </>
}

export default ExpenseProject;